import React, { useMemo } from "react";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import { CheckOutlined } from "@ant-design/icons";
import { CURRENCY } from "../../constants/DeliveryConstants";
import { useDeliveryAddress } from "../../contexts/DeliveryAddressProvider";
import { colors } from "../../utils/theme";
import { getFreeDeliveryFromPrice } from "../../helpers/region";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper/modules";

const UspBar = () => {
  const { t } = useTranslation();
  const { values, openModal } = useDeliveryAddress();

  // Een drempel voor iedereen sinds SooCool; de helper geeft hem ongeacht de
  // postcode. De dependency op de postcode blijft staan zodat de USP mee-update
  // als de klant later toch weer een postcode-afhankelijke drempel krijgt.
  const threshold = useMemo(() => {
    return getFreeDeliveryFromPrice(values?.zip);
  }, [values?.zip]);

  /*
    Geen land-forks meer. Hier stonden er twee:

    - een Belgie-USP "Lagere verzendkosten boven €100", die nooit ergens in de
      prijsberekening bestond en dus altijd een loze belofte was;
    - een gate die de gratis-verzending-USP alleen aan Nederland toonde, omdat
      Belgie destijds nooit gratis bezorgd kreeg.

    Sinds SooCool geldt er een tarief en een drempel voor elk adres, Belgie
    inbegrepen, dus de USP mag altijd mee.
  */
  const items = [
    {
      key: "free",
      text: t("header_usps.free_shipping", {
        amount: `${CURRENCY}${threshold}`,
      }),
      onClick: async () => {
        try {
          await openModal();
          // threshold will re-compute from context automatically
        } catch (e) {}
      },
      clickable: true,
    },
    {
      key: "frozen",
      text: t("header_usps.frozen_delivery"),
    },
    {
      key: "nextday",
      text: t("header_usps.next_day_delivery"),
    },
  ];

  return (
    <BannerWrapper aria-label="USP banner">
      <SwiperWrapper
        spaceBetween={0}
        slidesPerView={1}
        breakpoints={{
          768: { slidesPerView: 3 },
        }}
        loop
        autoplay={{ delay: 3500, disableOnInteraction: false }}
        modules={[Autoplay]}
        aria-label="USP slider"
      >
        {items.map((item) => (
          <SwiperSlide key={item.key}>
            <Usp
              as={item.onClick ? "button" : "div"}
              onClick={item.onClick || undefined}
              $clickable={!!item.onClick}
            >
              <CheckOutlined className="icon" style={{ margin: 0 }} />
              <span>{item.text}</span>
            </Usp>
          </SwiperSlide>
        ))}
      </SwiperWrapper>
    </BannerWrapper>
  );
};

export default UspBar;

const BannerWrapper = styled.div`
  width: 100%;
  background: ${colors.primary};
  color: #fff;
`;

const SwiperWrapper = styled(Swiper)`
  min-height: 38px;
  padding: 6px 0;
  align-content: center;

  .swiper-wrapper {
    align-items: center;
  }

  .swiper-slide {
    display: flex;
    justify-content: center;
  }
`;

const Usp = styled.button`
  appearance: none;
  border: 0;
  background: transparent;
  color: inherit;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  line-height: 1.3;
  cursor: ${(p) => (p.$clickable ? "pointer" : "default")} !important;
  padding: 4px 10px;

  .icon {
    font-size: 14px;
  }
`;
